import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Divider, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { GridColumnMenu, type GridColumnMenuProps } from "@mui/x-data-grid";
import type { Dispatch, SetStateAction, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "@/theme/tokens";
import { applyColumnSort, type MultiSortOption, type SortClickBehavior, type SortingState, type SortOrder, sortColumnsEqual } from "@/utils/multiColumnSorting";

/**
 * Sort items for the per-column "⋮" menu.
 *
 * The grid runs with MUI's own `disableColumnSorting`, because sorting is owned by `useMultiSort`.
 * The side effect was that the one menu attached to a column was the one place sorting wasn't — it
 * offered Filter and Hide and nothing else. These items put it back, driven by the same sort state
 * the headers and the sort popup use.
 */
function ColumnSortMenuItems<T>({
	colDef,
	hideMenu,
	sortState,
	setSortState,
	optionByField,
	headerClickMode,
	retainEntry,
	canFilterByColumn,
	onFilterByColumn,
	filterByHint,
}: {
	colDef: GridColumnMenuProps["colDef"];
	hideMenu: (event: SyntheticEvent) => void;
	sortState: SortingState<T>[];
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	optionByField: Map<keyof T, MultiSortOption<T>>;
	headerClickMode: SortClickBehavior;
	retainEntry: (entry: SortingState<T>) => boolean;
	canFilterByColumn?: (field: string) => boolean;
	onFilterByColumn?: (field: string) => void;
	filterByHint?: (field: string) => string | undefined;
}) {
	const { t } = useTranslation();
	const field = colDef.field as keyof T;
	// The menu offers sorting under exactly the two conditions the clickable header is injected under
	// (see `effectiveColumns` in SystemDataGrid): the column must be sortable, and it must have a
	// declared `MultiSortOption`. On a `sortable: false` column the items were a contradiction; on a
	// column with no option they seeded a sort entry that neither a header nor a popup row can reach,
	// so its ✕ was unreachable and `retainEntry` then carried it through every later header click.
	const sortOption = colDef.sortable === false ? undefined : optionByField.get(field);
	// `optionByField` is keyed off `sortColumns[0]`, so every option in it has a non-empty `sortColumns`.
	// Reading it back through the optional type is what gates the items — never a fallback key, since
	// inventing one is how the unreachable sort entry got created in the first place.
	const sortColumns = sortOption?.sortColumns;
	const activeOrder = sortColumns ? sortState.find((entry) => sortColumnsEqual(entry.sortColumns, sortColumns))?.sortOrder : undefined;

	const sortBy = (event: SyntheticEvent, sortOrder: SortOrder, columns: Array<keyof T>) => {
		applyColumnSort<T>({
			sortState,
			setSortState,
			columns,
			label: colDef.headerName ?? String(colDef.field),
			customSortFunction: sortOption?.customSortFunction,
			sortOrder,
			clickBehavior: headerClickMode,
			retainEntry,
		});
		hideMenu(event);
	};

	const showFilterBy = !!onFilterByColumn && (canFilterByColumn?.(String(colDef.field)) ?? false);
	const destinationHint = filterByHint?.(String(colDef.field));

	return (
		<>
			{sortColumns && (
				<>
					<MenuItem onClick={(event) => sortBy(event, "asc", sortColumns)} selected={activeOrder === "asc"} data-testid="grid-column-menu-sort-asc">
						<ListItemIcon>
							<ArrowUpwardIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t("system.dataGrid.sortAsc")}</ListItemText>
					</MenuItem>
					<MenuItem onClick={(event) => sortBy(event, "desc", sortColumns)} selected={activeOrder === "desc"} data-testid="grid-column-menu-sort-desc">
						<ListItemIcon>
							<ArrowDownwardIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t("system.dataGrid.sortDesc")}</ListItemText>
					</MenuItem>
				</>
			)}
			{showFilterBy && (
				<>
					{/* Only a separator when there is something above it to separate from. */}
					{sortColumns && <Divider />}
					<MenuItem
						onClick={(event) => {
							onFilterByColumn?.(String(colDef.field));
							hideMenu(event);
						}}
						data-testid="grid-column-menu-filter-by"
					>
						<ListItemIcon>
							<FilterAltOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t("system.dataGrid.filterBy", { column: colDef.headerName ?? String(colDef.field) })}</ListItemText>
						{destinationHint && (
							<Typography variant="caption" sx={{ color: INK.MUTED, ml: 2 }}>
								{destinationHint}
							</Typography>
						)}
					</MenuItem>
				</>
			)}
		</>
	);
}

/**
 * Builds the `columnMenu` slot component for a sortable grid. Returns MUI's own menu with the sort
 * items injected first via the `columnMenuUserItem` slot — everything else about the menu (and every
 * consumer that turns it off with `disableColumnMenu`) is untouched.
 */
export function buildSystemGridColumnMenu<T>({
	sortState,
	setSortState,
	optionByField,
	headerClickMode,
	retainEntry,
	canFilterByColumn,
	onFilterByColumn,
	filterByHint,
}: {
	sortState: SortingState<T>[];
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	optionByField: Map<keyof T, MultiSortOption<T>>;
	headerClickMode: SortClickBehavior;
	retainEntry: (entry: SortingState<T>) => boolean;
	canFilterByColumn?: (field: string) => boolean;
	onFilterByColumn?: (field: string) => void;
	filterByHint?: (field: string) => string | undefined;
}) {
	return function SystemGridColumnMenu(props: GridColumnMenuProps) {
		return (
			<GridColumnMenu
				{...props}
				slots={{
					/**
					 * Drop MUI's own "Filter" item when the page owns filtering.
					 *
					 * Otherwise the menu offers two filter entries side by side — ours, which hands off to the
					 * page's filter rail, and MUI's, which opens a separate per-column filter panel holding
					 * criteria the rail knows nothing about. Two filtering surfaces on one table means a
					 * user can narrow the grid in a way the rail's counts and "Clear all" cannot see or undo.
					 */
					...(onFilterByColumn ? { columnMenuFilterItem: null } : {}),
					columnMenuUserItem: () => (
						<ColumnSortMenuItems<T>
							colDef={props.colDef}
							hideMenu={props.hideMenu}
							sortState={sortState}
							setSortState={setSortState}
							optionByField={optionByField}
							headerClickMode={headerClickMode}
							retainEntry={retainEntry}
							canFilterByColumn={canFilterByColumn}
							onFilterByColumn={onFilterByColumn}
							filterByHint={filterByHint}
						/>
					),
				}}
				slotProps={{
					// Ahead of MUI's own items (filter is 10, columns 20) so sorting reads first.
					columnMenuUserItem: { displayOrder: 5 },
				}}
			/>
		);
	};
}
